import { Message } from '@/interfaces/message'
import { listTicketMessagesQuery } from '@/queries/ticket.query'
import { TicketService } from '@/services/ticket.service'
import { QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export type ChatsDialogType = 'new' | 'finalize' | 'editContact' | 'transfer'
export type ChatsTabType = 'atendendo' | 'agente' | 'esperando' | 'finalizados'
export type MessageStatus = 'pending' | 'failed' | 'sent'

type SerializedFormData = {
  content?: string
  type: string
  caption?: string
}

type PendingMessage = {
  tempId: string
  ticketId: number
  formData: FormData | SerializedFormData
  timestamp: string
  status: MessageStatus
}

type ChatState = {
  currentTicketId: number | null
  activeTab: ChatsTabType
  dialog: ChatsDialogType | null
  lastFocusedTicketId: number | null
  pendingMessages: Map<string, PendingMessage>
  messageStatuses: Map<string, MessageStatus>
  queryClient: QueryClient | null
  unreadMessages: Map<number, number>
}

type ChatActions = {
  focusTicket: (id: number) => void
  clearTicket: () => void
  setActiveTab: (tab: ChatsTabType) => void
  setDialog: (dialog: ChatsDialogType | null) => void
  addPendingMessage: (
    tempId: string,
    ticketId: number,
    formData: FormData
  ) => void
  markMessageFailed: (tempId: string) => void
  markMessageSent: (tempId: string) => void
  retryMessage: (tempId: string) => Promise<void>
  getMessageStatus: (messageId: string) => MessageStatus
  setQueryClient: (queryClient: QueryClient) => void
  incrementUnread: (ticketId: number) => void
  clearUnread: (ticketId: number) => void
  getUnreadCount: (ticketId: number) => number
}

export type ChatStore = ChatState & ChatActions

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      currentTicketId: null,
      activeTab: 'atendendo',
      dialog: null,
      lastFocusedTicketId: null,
      pendingMessages: new Map(),
      messageStatuses: new Map(),
      queryClient: null,
      unreadMessages: new Map(),

      setQueryClient: (queryClient: QueryClient) => {
        set({ queryClient })
      },

      addPendingMessage: (
        tempId: string,
        ticketId: number,
        formData: FormData
      ) => {
        const pendingMessages = new Map(get().pendingMessages)
        const messageStatuses = new Map(get().messageStatuses)
        pendingMessages.set(tempId, {
          tempId,
          ticketId,
          formData,
          timestamp: new Date().toISOString(),
          status: 'pending',
        })
        messageStatuses.set(tempId, 'pending')
        set({ pendingMessages, messageStatuses })
      },

      markMessageFailed: (tempId: string) => {
        const pendingMessages = new Map(get().pendingMessages)
        const messageStatuses = new Map(get().messageStatuses)
        const message = pendingMessages.get(tempId)
        if (message) {
          message.status = 'failed'
          pendingMessages.set(tempId, message)
        }
        messageStatuses.set(tempId, 'failed')
        set({ pendingMessages, messageStatuses })
      },

      markMessageSent: (tempId: string) => {
        const pendingMessages = new Map(get().pendingMessages)
        const messageStatuses = new Map(get().messageStatuses)
        pendingMessages.delete(tempId)
        messageStatuses.set(tempId, 'sent')
        set({ pendingMessages, messageStatuses })
      },

      retryMessage: async (tempId: string) => {
        const { pendingMessages, messageStatuses, queryClient } = get()
        const message = pendingMessages.get(tempId)
        if (!message || !queryClient) return

        const newPendingMessages = new Map(pendingMessages)
        const updatedMessage = {
          ...message,
          status: 'pending' as const,
          timestamp: new Date().toISOString(),
        }
        newPendingMessages.set(tempId, updatedMessage)

        const newMessageStatuses = new Map(messageStatuses)
        newMessageStatuses.set(tempId, 'pending')
        set({
          messageStatuses: newMessageStatuses,
          pendingMessages: newPendingMessages,
        })

        const queryKey = listTicketMessagesQuery(message.ticketId).queryKey

        let formData: FormData
        if (message.formData instanceof FormData) {
          formData = message.formData
        } else {
          formData = new FormData()
          formData.append('type', message.formData.type)
          if (message.formData.content) {
            formData.append('content', message.formData.content)
          }
          if (message.formData.caption) {
            formData.append('caption', message.formData.caption)
          }
        }

        try {
          const response = await TicketService.sendMessage(
            message.ticketId,
            formData
          )
          queryClient.setQueryData<Message[]>(queryKey, (old = []) => {
            const tempMessage = old.find((m) => m.id === tempId)
            if (tempMessage?.mediaUrl?.startsWith('blob:')) {
              URL.revokeObjectURL(tempMessage.mediaUrl)
            }
            if (
              tempMessage?.mediaThumbnailUrl?.startsWith('blob:') &&
              tempMessage.mediaThumbnailUrl !== tempMessage.mediaUrl
            ) {
              URL.revokeObjectURL(tempMessage.mediaThumbnailUrl)
            }
            const withoutTemp = old.filter((m) => m.id !== tempId)
            const alreadyExists = withoutTemp.some((m) => m.id === response.id)
            if (alreadyExists) return withoutTemp
            return [...withoutTemp, response]
          })
          get().markMessageSent(tempId)
          toast.success('Mensagem enviada com sucesso')
        } catch {
          get().markMessageFailed(tempId)
          toast.error('Erro ao reenviar mensagem')
        }
      },

      getMessageStatus: (messageId: string) => {
        const { messageStatuses } = get()
        return messageStatuses.get(messageId) || 'sent'
      },

      focusTicket: (id: number) => {
        const unreadMessages = new Map(get().unreadMessages)
        unreadMessages.delete(id)
        set({
          currentTicketId: id,
          lastFocusedTicketId: id,
          unreadMessages,
        })
      },

      clearTicket: () => {
        set({ currentTicketId: null })
      },

      setActiveTab: (tab: ChatsTabType) => {
        set({ activeTab: tab })
      },

      setDialog: (dialog: ChatsDialogType | null) => {
        set({ dialog })
      },

      incrementUnread: (ticketId: number) => {
        const { currentTicketId, unreadMessages } = get()
        if (currentTicketId === ticketId) return
        const newUnreadMessages = new Map(unreadMessages)
        const current = newUnreadMessages.get(ticketId) || 0
        newUnreadMessages.set(ticketId, current + 1)
        set({ unreadMessages: newUnreadMessages })
      },

      clearUnread: (ticketId: number) => {
        const unreadMessages = new Map(get().unreadMessages)
        unreadMessages.delete(ticketId)
        set({ unreadMessages })
      },

      getUnreadCount: (ticketId: number) => {
        const { unreadMessages } = get()
        return unreadMessages.get(ticketId) || 0
      },
    }),
    {
      name: 'chat-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentTicketId: state.currentTicketId,
        activeTab: state.activeTab,
        dialog: state.dialog,
        lastFocusedTicketId: state.lastFocusedTicketId,
        pendingMessages: Array.from(state.pendingMessages.entries()).map(
          ([id, msg]) => {
            if (msg.formData instanceof FormData) {
              const serialized: SerializedFormData = {
                type: (msg.formData.get('type') as string) || 'text',
                content: msg.formData.get('content') as string | undefined,
                caption: msg.formData.get('caption') as string | undefined,
              }
              return [id, { ...msg, formData: serialized }]
            }
            return [id, msg]
          }
        ),
        messageStatuses: Array.from(state.messageStatuses.entries()),
        unreadMessages: Array.from(state.unreadMessages.entries()),
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<ChatState> & {
          pendingMessages?: [string, PendingMessage][]
          messageStatuses?: [string, MessageStatus][]
          unreadMessages?: [number, number][]
        }
        return {
          ...currentState,
          ...persisted,
          pendingMessages: new Map(persisted.pendingMessages ?? []),
          messageStatuses: new Map(persisted.messageStatuses ?? []),
          unreadMessages: new Map(persisted.unreadMessages ?? []),
        }
      },
    }
  )
)

export const selectCurrentTicketId = (state: ChatState) => state.currentTicketId
export const selectActiveTab = (state: ChatState) => state.activeTab
export const selectDialog = (state: ChatState) => state.dialog
export const selectLastFocusedTicketId = (state: ChatState) =>
  state.lastFocusedTicketId
export const selectMessageStatuses = (state: ChatState) => state.messageStatuses

export const selectFocusTicket = (state: ChatActions) => state.focusTicket
export const selectClearTicket = (state: ChatActions) => state.clearTicket
export const selectSetActiveTab = (state: ChatActions) => state.setActiveTab
export const selectSetDialog = (state: ChatActions) => state.setDialog
export const selectAddPendingMessage = (state: ChatActions) =>
  state.addPendingMessage
export const selectMarkMessageFailed = (state: ChatActions) =>
  state.markMessageFailed
export const selectMarkMessageSent = (state: ChatActions) =>
  state.markMessageSent
export const selectRetryMessage = (state: ChatActions) => state.retryMessage
export const selectGetMessageStatus = (state: ChatActions) =>
  state.getMessageStatus
export const selectSetQueryClient = (state: ChatActions) => state.setQueryClient
export const selectIncrementUnread = (state: ChatActions) =>
  state.incrementUnread
export const selectClearUnread = (state: ChatActions) => state.clearUnread
export const selectGetUnreadCount = (state: ChatActions) => state.getUnreadCount
export const selectUnreadMessages = (state: ChatState) => state.unreadMessages
